import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, PackageCheck, FileCheck2, Layers, Boxes, Tags, ScrollText, Settings, LogOut, Truck, CalendarRange, KeyRound, FlaskConical, FileUp, Network, History, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  ["/", "Dashboard", LayoutDashboard, "nav-dashboard"],
  ["/orders", "Customer Orders", PackageCheck, "nav-orders"],
  ["/sap-import", "Order Import (SAP / Coupa)", FileUp, "nav-sap-import"],
  ["/replenishment", "Replenishment", Truck, "nav-replenishment"],
  ["/outputs", "Output Review", FileCheck2, "nav-outputs"],
  ["/raw-materials", "Raw Materials", Layers, "nav-raw-materials"],
  ["/inventory", "Inventory", Boxes, "nav-inventory"],
  ["/planning", "Forecast & Planning", CalendarRange, "nav-planning"],
  ["/items", "Item Master", Tags, "nav-items"],
  ["/edi", "EDI Log", ScrollText, "nav-edi-log"],
  ["/partners", "Trading Partners", Network, "nav-partners"],
  ["/change-log", "Change Log", History, "nav-change-log"],
  ["/settings", "Partner Settings", Settings, "nav-settings"],
];

const NAV_UP = [
  ["/uploads", "Upload POs", FileUp, "nav-uploads"],
  ["/my-summary", "My summary", PackageCheck, "nav-my-summary"],
];
const UP_PATHS = ["/uploads", "/my-summary"];

const NavItem = ([to, label, Icon, tid]) => (
  <NavLink
    key={to}
    to={to}
    end={to === "/"}
    data-testid={tid}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
        isActive ? "bg-emerald-500/10 text-emerald-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
      }`
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="whitespace-nowrap">{label}</span>
  </NavLink>
);

export default function Layout() {
  const { user, signOut, setUser } = useAuth();
  const [pwd, setPwd] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    if (!user || user.role === "uploader") return;
    const load = () => api.get("/settings").then((r) => setTestMode(r.data.test_mode === true)).catch(() => {});
    load();
    window.addEventListener("ep-settings", load);
    return () => window.removeEventListener("ep-settings", load);
  }, [user, loc.pathname]);
  if (user === null) return <div className="min-h-screen bg-[#17191F]" />;
  if (!user) return <Navigate to="/login" replace />;
  const up = user.role === "uploader";
  if (up && !UP_PATHS.includes(loc.pathname)) return <Navigate to="/uploads" replace />;
  const forced = !!user.must_change_password;

  return (
    <div className="min-h-screen bg-[#17191F] text-slate-200 md:flex">
      <aside className="border-b border-slate-800 bg-[#2F3646] md:sticky md:top-0 md:h-screen md:w-60 md:shrink-0 md:border-b-0 md:border-r">
        <div className="px-5 py-5">
          <img src="/brand/ep_logo_red.png" alt="EP Group" className="h-7 w-auto" data-testid="sidebar-logo" />
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">EDI Order Hub</div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible">{(up ? NAV_UP : user.role === "manager" ? [...NAV, ["/mailboxes", "Mailbox Pickup", Mail, "nav-mailboxes"], ["/users", "Users", KeyRound, "nav-users"]] : NAV).map(NavItem)}</nav>
      </aside>
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-[#1F232C]/90 px-6 py-3 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="hidden font-mono text-[11px] uppercase tracking-widest text-slate-500 sm:block">{up ? "PO upload" : "Group O xDock · XD1 · XD2 · XD3"}</div>
            {testMode && !up && (
              <Link to="/settings" data-testid="header-test-mode-badge" className="inline-flex items-center gap-1.5 rounded-sm border border-amber-500/40 bg-amber-500/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-300">
                <FlaskConical className="h-3 w-3" /> Test mode · nothing sent to Group O
              </Link>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm text-slate-100" data-testid="header-user-name">{user.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-400" data-testid="header-user-role">{up ? "PO upload" : "Administrator"}</div>
            </div>
            <ThemeToggle />
            <button onClick={() => setPwd(true)} data-testid="change-password-btn" className="rounded-sm p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white">
              <KeyRound className="h-4 w-4" />
              <span className="sr-only">Change password</span>
            </button>
            <button onClick={signOut} data-testid="logout-btn" className="rounded-sm p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white">
              <LogOut className="h-4 w-4" />
              <span className="sr-only">Log out</span>
            </button>
          </div>
        </header>
        <main className="p-6 sm:p-8">
          <Outlet />
        </main>
        <ChangePasswordDialog open={pwd || forced} forced={forced} onClose={(ok) => { setPwd(false); if (ok) setUser({ ...user, must_change_password: false }); }} />
      </div>
    </div>
  );
}
